import React, { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex, Hide } from '@qga/roo-ui/components';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import styled from '@emotion/styled';
import { getPropertyCardsData, getHasError, getResultsRegionCenter } from 'store/search/searchSelectors';
import { getQueryLocation } from 'store/router/routerSelectors';
import { mapPropertyMarkerClick, mapRedoSearchClick, updateQuery, mapLocationClick } from 'store/search/searchActions';
import { setMapHoveredPropertyId, setMapActivePropertyId } from 'store/ui/uiActions';
import GoogleMapWithUI from './GoogleMapWithUI';
import { useBreakpoints } from 'hooks/useBreakpoints';
import FixedPropertyCard from 'components/MapSearchPage/FixedPropertyCard';
import PropertyMarker from 'components/MapSearchPage/PropertyMarker';
import PositionedPropertyCard from 'components/MapSearchPage/PositionedPropertyCard';
import MapFiltersBar from 'components/MapSearchPage/MapFiltersBar';
import {
  immutableReverse,
  positionMapToResults,
  panToBounds,
  getBounds,
  createBoundFromPoints,
  getCenter,
} from 'components/maps/GoogleMap/mapUtils';
import ResultsList from 'components/MapSearchPage/ResultsList';
import { getMapActivePropertyId } from 'store/ui/uiSelectors';
import { getBrowser } from 'store/userEnvironment/userEnvironmentSelectors';
import { isLegacyBrowser } from 'lib/browser/isLegacyBrowser';

const GOOGLE_MAPS_TOP_RIGHT_POSITION = 3;

const Wrapper = styled(Box)`
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const mapOptions = {
  gestureHandling: 'greedy',
  clickableIcons: false,
  disableDefaultUI: false,
  maxZoom: 22,
  minZoom: 3,
  mapTypeControlOptions: {
    position: GOOGLE_MAPS_TOP_RIGHT_POSITION,
  },
  styles: [
    { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  ],
};

const phoneMapOptions = { ...mapOptions, disableDefaultUI: true };

const defaultPropertyFilter = () => () => true;

const ResultsMap = React.memo(() => {
  const rawPropertyCardsData = useSelector(getPropertyCardsData);
  const searchHasError = useSelector(getHasError);
  const regionCenter = useSelector(getResultsRegionCenter) || {};
  const { isLessThanBreakpoint } = useBreakpoints();
  const activePropertyId = useSelector(getMapActivePropertyId);
  const [map, setMap] = useState({});
  const [propertyFilter, setPropertyFilter] = useState(defaultPropertyFilter);
  const dispatch = useDispatch();
  const location = useSelector(getQueryLocation);

  const propertyCardsData = useMemo(() => filter(rawPropertyCardsData, propertyFilter), [rawPropertyCardsData, propertyFilter]);

  const browser = useSelector(getBrowser);
  const displayGoogleMap = !isLegacyBrowser(browser?.name);

  const activeProperty = useMemo(() => find(propertyCardsData, ['id', activePropertyId]), [propertyCardsData, activePropertyId]);

  const reversedPropertyCardsData = useMemo(() => immutableReverse(propertyCardsData), [propertyCardsData]);

  useEffect(() => {
    if (activePropertyId && !activeProperty) {
      dispatch(setMapActivePropertyId(null));
    }
  }, [activeProperty, activePropertyId, dispatch, propertyCardsData]);

  const onPropertyMarkerClick = useCallback(
    (property) => {
      dispatch(mapPropertyMarkerClick(property));
    },
    [dispatch],
  );

  const onMapRedoSearchClick = useCallback(() => {
    dispatch(mapRedoSearchClick());
  }, [dispatch]);

  const onUpdateQuery = useCallback(
    (data) => {
      dispatch(updateQuery(data));
    },
    [dispatch],
  );

  const center = getCenter(regionCenter);

  const handleMapClicked = useCallback(() => {
    dispatch(setMapActivePropertyId(null));
  }, [dispatch]);

  const handleMarkerClick = useCallback(
    ({ id, lat, lng, title }) => {
      if (!isEmpty(map)) {
        const { gMap, gMaps } = map;
        onPropertyMarkerClick({ propertyId: id });
        const bounds = createBoundFromPoints(gMaps, { lat, lng });
        panToBounds(gMap, bounds, { top: 400, left: 200, right: 200 });
        dispatch(setMapActivePropertyId(id));
        dispatch(
          mapLocationClick({
            groupName: `Hotels in ${location}`,
            itemName: title,
          }),
        );
      }
    },
    [map, onPropertyMarkerClick, dispatch, location],
  );

  const handleResultHover = useCallback(
    ({ id, lat, lng }) => {
      if (!isEmpty(map)) {
        const { gMap, gMaps } = map;
        const bounds = createBoundFromPoints(gMaps, { lat, lng });
        panToBounds(gMap, bounds, { top: 100, left: 100, right: 100, bottom: 100 });
        dispatch(setMapHoveredPropertyId(id));
      }
    },
    [dispatch, map],
  );

  const handleRedoSearchClick = useCallback(() => {
    setPropertyFilter(defaultPropertyFilter);
    const { gMap } = map;
    const bounds = getBounds(gMap);
    onMapRedoSearchClick();
    onUpdateQuery({
      swLat: bounds.south,
      swLng: bounds.west,
      neLat: bounds.north,
      neLng: bounds.east,
    });
  }, [onMapRedoSearchClick, onUpdateQuery, map]);

  const handleMapLoaded = useCallback(
    ({ gMap, gMaps }) => {
      if (reversedPropertyCardsData.length > 0) {
        positionMapToResults(gMap, gMaps, reversedPropertyCardsData);
      }
      setMap({ gMap: gMap, gMaps: gMaps });
    },
    [reversedPropertyCardsData, setMap],
  );

  const handleRequeryMap = ({ query, isWithinPolygon }) => {
    setPropertyFilter(isWithinPolygon);
    onUpdateQuery(query);
  };

  const propertyCardsList = useMemo(
    () =>
      reversedPropertyCardsData.map((property, index) => (
        <Box key={`property-card-${index}`} lat={property.latitude} lng={property.longitude}>
          <PropertyMarker
            {...property}
            onClick={handleMarkerClick}
            active={property.id === activePropertyId}
            title={property.propertyName}
          />
          {activePropertyId === property.id && (
            <Hide xs sm>
              <PositionedPropertyCard {...property} showCampaignMessage />
            </Hide>
          )}
        </Box>
      )),
    [reversedPropertyCardsData, handleMarkerClick, activePropertyId],
  );

  return (
    <Fragment>
      <MapFiltersBar />
      <Flex height="calc(100% - 60px)" position="absolute" top={60} width="100%">
        <Box display={['none', 'none', 'flex']}>
          <ResultsList results={propertyCardsData} onResultHover={handleResultHover} />
        </Box>
        <Flex width="100%" position="relative" flexDirection="column">
          {displayGoogleMap && (
            <GoogleMapWithUI
              center={center}
              error={searchHasError}
              width="100%"
              height="100%"
              options={isLessThanBreakpoint(0) ? phoneMapOptions : mapOptions}
              onMapAPILoaded={handleMapLoaded}
              onClick={handleMapClicked}
              onUpdateQuery={handleRedoSearchClick}
              onTouchSearchUpdated={handleRequeryMap}
            >
              {propertyCardsList}
            </GoogleMapWithUI>
          )}
          {!displayGoogleMap && (
            <Flex height="100%" alignItems="center" justifyContent="center">
              Sorry, map search can&apos;t be displayed right now.
            </Flex>
          )}
        </Flex>
        {activeProperty ? (
          <Wrapper display={['block', 'block', 'none']}>
            <FixedPropertyCard {...activeProperty} />
          </Wrapper>
        ) : null}
      </Flex>
    </Fragment>
  );
});

ResultsMap.displayName = 'ResultsMap';

export default ResultsMap;
